import { useState } from 'react';
import { FileText, Download, Printer, ChevronDown } from 'lucide-react';

interface DocumentActionsProps {
  reservationId: number;
  type: 'voucher' | 'guest-form' | 'invoice' | 'receipt';
  title: string;
  color: string;
}

export default function DocumentActions({ reservationId, type, title, color }: DocumentActionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateDocument = async (action: 'view' | 'download' | 'print') => {
    setIsGenerating(true);
    try {
      const response = await fetch(`/api/reservations/${reservationId}/${type}`);
      if (response.ok) {
        const html = await response.text();
        
        if (action === 'view') {
          const newWindow = window.open('', '_blank');
          if (newWindow) {
            newWindow.document.write(html);
            newWindow.document.close();
          }
        } else if (action === 'download') {
          const blob = new Blob([html], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${type}-${reservationId}.html`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else if (action === 'print') {
          const newWindow = window.open('', '_blank');
          if (newWindow) {
            newWindow.document.write(html);
            newWindow.document.close();
            newWindow.print();
          }
        }
      }
    } catch (error) {
      console.error('Erro ao gerar documento:', error);
    } finally {
      setIsGenerating(false);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-2 ${color} transition-colors flex items-center space-x-1`}
        title={title}
        disabled={isGenerating}
      >
        <FileText className="w-4 h-4" />
        <ChevronDown className="w-3 h-3" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
          <div className="py-1">
            <button
              onClick={() => generateDocument('view')}
              className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              disabled={isGenerating}
            >
              <FileText className="w-4 h-4" />
              <span>Visualizar</span>
            </button>
            <button
              onClick={() => generateDocument('download')}
              className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              disabled={isGenerating}
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </button>
            <button
              onClick={() => generateDocument('print')}
              className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              disabled={isGenerating}
            >
              <Printer className="w-4 h-4" />
              <span>Imprimir</span>
            </button>
          </div>
        </div>
      )}

      {isOpen && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
